import { z } from "zod";
import { findDistrict } from "../geo";
import { getPack } from "../packs";
import { bdMobileSchema, localeSchema } from "./auth";

/**
 * NBR Business Identification Number. Formats differ between BIN generations, so only the digit
 * count is checked here; the VAT consultant sign-off (SRS 2.5) confirms the exact rule.
 */
export const binSchema = z
  .string()
  .trim()
  .regex(/^\d{9,13}$/, "BIN must be 9 to 13 digits");

export const addressSchema = z
  .object({
    divisionId: z.string().min(1),
    districtId: z.string().min(1),
    upazila: z.string().trim().min(2).max(80),
    line: z.string().trim().min(3).max(200),
  })
  .refine((a) => findDistrict(a.divisionId, a.districtId) !== null, {
    message: "Unknown division or district",
    path: ["districtId"],
  });
export type Address = z.infer<typeof addressSchema>;

export const createTenantSchema = z.object({
  name: z.string().trim().min(2).max(120),
  packId: z.string().refine((id) => getPack(id) !== undefined, "Unknown business type"),
  phone: bdMobileSchema,
  address: addressSchema,
  bin: binSchema.optional(),
  locale: localeSchema.default("bn"),
});
export type CreateTenantInput = z.input<typeof createTenantSchema>;

export const branchSchema = z.object({
  id: z.string(),
  code: z.string(),
  name: z.string(),
  phone: z.string().nullable(),
  address: z.string().nullable(),
  isMain: z.boolean(),
  active: z.boolean(),
});
export type Branch = z.infer<typeof branchSchema>;

/** Loyalty points (Phase 7): earned on what customers spend, redeemed as a discount. */
export const loyaltySettingsSchema = z.object({
  enabled: z.boolean(),
  /** Spend that earns one point, poisha: 10_000 = 1 point per ৳100. */
  spendPerPoint: z.number().int().min(100).max(10_000_000),
  /** What one point is worth when redeemed, poisha. */
  pointValue: z.number().int().min(1).max(100_000),
  /** Fewest points a customer can redeem at once. */
  minRedeem: z.number().int().min(1).max(1_000_000),
});
export type LoyaltySettings = z.infer<typeof loyaltySettingsSchema>;
export const LOYALTY_OFF: LoyaltySettings = {
  enabled: false,
  spendPerPoint: 10_000,
  pointValue: 100,
  minRedeem: 50,
};

export const tenantSettingsSchema = z.object({
  businessName: z.string(),
  packId: z.string(),
  packVersion: z.number().int(),
  phone: z.string(),
  address: addressSchema,
  bin: z.string().nullable(),
  locale: localeSchema,
  digits: z.enum(["latn", "beng"]),
  fiscalYearStartMonth: z.number().int().min(1).max(12),
  cashRounding: z.union([z.literal(0), z.literal(50), z.literal(100)]),
  pricesIncludeVat: z.boolean(),
  /** FR-INV-05: blocked by default; when allowed, shortfalls settle on the next receipt. */
  allowNegativeStock: z.boolean().default(false),
  /** Largest discount (bps of the bill) a role without sale.discount.above_limit may give. */
  posDiscountLimitBps: z.number().int().min(0).max(10_000).default(1_000),
  /** Printed at the bottom of receipts, e.g. return policy. */
  receiptNote: z.string().max(200).default(""),
  /** Text the shopper when an online order is confirmed, shipped or cancelled (FR-WEB-05). */
  orderSms: z.boolean().default(true),
  /** Email owners a summary of the week every Saturday morning. */
  weeklySummary: z.boolean().default(true),
  loyalty: loyaltySettingsSchema.default(LOYALTY_OFF),
  modules: z.record(z.string(), z.boolean()),
  onboarding: z.object({
    completedSteps: z.array(z.string()),
    dismissed: z.boolean(),
  }),
});
export type TenantSettings = z.infer<typeof tenantSettingsSchema>;

export const settingsUpdateSchema = z
  .object({
    businessName: z.string().trim().min(2).max(120),
    phone: bdMobileSchema,
    address: addressSchema,
    bin: binSchema.nullable(),
    vatEnabled: z.boolean(),
    pricesIncludeVat: z.boolean(),
    cashRounding: z.union([z.literal(0), z.literal(50), z.literal(100)]),
    allowNegativeStock: z.boolean(),
    posDiscountLimitBps: z.number().int().min(0).max(10_000),
    fiscalYearStartMonth: z.number().int().min(1).max(12),
    locale: localeSchema,
    digits: z.enum(["latn", "beng"]),
    receiptNote: z.string().trim().max(200),
    orderSms: z.boolean(),
    weeklySummary: z.boolean(),
    loyalty: loyaltySettingsSchema,
  })
  .partial()
  .refine((v) => Object.keys(v).length > 0, "Nothing to update");
export type SettingsUpdate = z.output<typeof settingsUpdateSchema>;
